import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { READINESS_EXIT_DURATION_MS } from 'modules/overlay-v2/domain/constants/readiness';
import { OverlayReadinessStatus } from 'modules/overlay-v2/domain/interfaces/readiness';
import { theme } from 'styles/theme/theme';

/**
 * Уход разбит на две части: сначала оператор успевает прочитать «Всё готово», и только
 * потом карточка исчезает. Сумма равна `READINESS_EXIT_DURATION_MS` — столько же чеклист
 * держится смонтированным, поэтому CSS и React не расходятся.
 */
const EXIT_ANIMATION_MS = 300;
const EXIT_DELAY_MS = READINESS_EXIT_DURATION_MS - EXIT_ANIMATION_MS;

export const STATUS_COLORS: Record<OverlayReadinessStatus, string> = {
  pending: theme.colors.WhiteGray50,
  running: theme.colors.AccentNaplesYellow,
  done: theme.colors.submit,
  warning: theme.colors.warning,
  failed: theme.colors.error,
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: scale(0.94) translateY(16px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const cardOut = keyframes`
  from { opacity: 1; transform: scale(1) translateY(0); }
  to { opacity: 0; transform: scale(0.94) translateY(16px); }
`;

const rowIn = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(220%); }
`;

const draw = keyframes`
  to { stroke-dashoffset: 0; }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  75% { transform: translateX(3px); }
`;

const glow = keyframes`
  0%, 100% { opacity: 0.10; }
  50% { opacity: 0.22; }
`;

export const Backdrop = styled.div<{ isClosing: boolean }>`
  /* Фиксированный слой поверх страницы: пока чеклист доигрывает уход, табло и панель
     уже смонтированы под ним и успевают отрисоваться до того, как их откроют. */
  position: fixed;
  inset: 0;
  z-index: ${theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  pointer-events: ${({ isClosing }) => (isClosing ? 'none' : 'auto')};
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} ${EXIT_ANIMATION_MS}ms ease-in-out
    ${({ isClosing }) => (isClosing ? EXIT_DELAY_MS : 0)}ms forwards;

  /* Оверлей висит в OBS часами, панель — на ноутбуке оператора. Если человек попросил
     систему убрать анимации, чеклист замирает: постоянное движение на экране мешает. */
  @media (prefers-reduced-motion: reduce) {
    &,
    *,
    *::before,
    *::after {
      animation: none !important;
      transition: none !important;
    }
  }
`;

export const Card = styled.section<{ isClosing: boolean }>`
  width: min(440px, 100%);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  padding: 24px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.WhiteGray20};
  background: linear-gradient(160deg, ${theme.colors.SurfaceSpaceCadet50} 0%, ${theme.colors.SurfaceRaisinBlack50} 100%);
  box-shadow: 0 24px 60px ${theme.colors.DarkBlue900};
  animation: ${({ isClosing }) => (isClosing ? cardOut : cardIn)} ${({ isClosing }) => (isClosing ? EXIT_ANIMATION_MS : 420)}ms
    cubic-bezier(0.16, 1, 0.3, 1) ${({ isClosing }) => (isClosing ? EXIT_DELAY_MS : 0)}ms forwards;
`;

/**
 * Шапка с логотипом. Знак повторяет `UDLogo` («Im» жёлтым, «Game» белым), но без `Link`:
 * чеклист висит в OBS, кликать по нему некому, а роутера в его окружении может и не быть.
 * Тонкая линия отделяет бренд от содержимого, не споря с прогресс-баром ниже.
 */
export const Brand = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 16px;
  margin-bottom: 18px;
  border-bottom: 1px solid ${theme.colors.WhiteGray20};
  font-size: 22px;
  line-height: 1;
  letter-spacing: 0.01em;
  color: ${theme.colors.InkOnDark};
  user-select: none;

  & span {
    color: ${theme.colors.DarkYellow};
  }
`;

export const ProgressTrack = styled.div`
  position: relative;
  height: 6px;
  margin-top: 14px;
  border-radius: 999px;
  overflow: hidden;
  background: ${theme.colors.WhiteGray20};
`;

export const ProgressFill = styled.div<{ percent: number; hasFailed: boolean }>`
  position: relative;
  height: 100%;
  width: ${({ percent }) => percent}%;
  border-radius: inherit;
  overflow: hidden;
  background: ${({ hasFailed }) =>
    hasFailed
      ? theme.colors.error
      : `linear-gradient(90deg, ${theme.colors.DarkYellow300} 0%, ${theme.colors.AccentNaplesYellow} 100%)`};
  transition: width 0.45s cubic-bezier(0.16, 1, 0.3, 1);

  /* Бегущий блик — только пока полоса не доехала: на готовом чеклисте он лишний шум. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, ${theme.colors.SurfaceWhite}, transparent);
    opacity: 0.35;
    animation: ${shimmer} 1.6s ease-in-out infinite;
    display: ${({ percent, hasFailed }) => (percent >= 100 || hasFailed ? 'none' : 'block')};
  }
`;

export const StepList = styled.ol`
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  list-style: none;
`;

export const StepRow = styled.li<{ stepStatus: OverlayReadinessStatus; index: number }>`
  position: relative;
  display: grid;
  grid-template-columns: 22px 1fr;
  align-items: start;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 12px;
  opacity: ${({ stepStatus }) => (stepStatus === 'pending' ? 0.5 : 1)};
  transition: opacity 0.3s ease-out;
  animation: ${rowIn} 0.38s cubic-bezier(0.16, 1, 0.3, 1) backwards;
  animation-delay: ${({ index }) => index * 55}ms;

  /* Подложка активного/проблемного шага. Отдельным слоем с прозрачностью, чтобы взять
     цвет из темы, а не выдумывать полупрозрачный оттенок хардкодом. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${({ stepStatus }) => STATUS_COLORS[stepStatus]};
    opacity: ${({ stepStatus }) => (stepStatus === 'running' || stepStatus === 'failed' ? 0.12 : 0)};
    transition: opacity 0.3s ease-out;
    animation: ${({ stepStatus }) => (stepStatus === 'running' ? glow : 'none')} 2s ease-in-out infinite;
    pointer-events: none;
  }
`;

export const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const StatusBadge = styled.span<{ stepStatus: OverlayReadinessStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  color: ${({ stepStatus }) => STATUS_COLORS[stepStatus]};

  svg {
    width: 100%;
    height: 100%;
    animation: ${({ stepStatus }) => (stepStatus === 'running' ? spin : 'none')} 1s linear infinite;
  }

  /* Галочка прорисовывается, а не появляется рывком: 24 — длина пути «check» в lucide. */
  &.done svg path {
    stroke-dasharray: 24;
    stroke-dashoffset: 24;
    animation: ${draw} 0.35s ease-out forwards;
  }

  &.failed svg {
    animation: ${shake} 0.4s ease-in-out;
  }
`;

export const HintBox = styled.div`
  margin-top: 2px;
  padding-left: 10px;
  border-left: 2px solid ${theme.colors.WhiteGray20};
`;

export const RetryButton = styled.button`
  align-self: flex-start;
  margin-top: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  font-family: var(--font-display);
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.DarkBlue};
  background: ${theme.colors.AccentNaplesYellow};
  transition:
    transform 0.15s ease-out,
    filter 0.15s ease-out;

  &:hover {
    filter: brightness(1.08);
  }

  &:active {
    transform: scale(0.96);
  }
`;
